import { Badge } from '@navinc/base-react-components/wayfinder'
import { getBlogArticleParentPath } from '@navinc/common'
import { CMSVersionDraft } from '@navinc/common'

import { CmsMediaFragment } from '../../_graphql/media.js'
import { useShowMoreLess } from '../../_hooks/use-show-more-less.js'
import { PLACEHOLDER_IMAGE_URL } from '../../const.js'
import { cmsGraphql, FragmentType, getFragmentData } from '../../lib/cms/index.js'
import { Gutter } from '../Gutter/index.js'
import { getMediaSizeUrlWithFallback } from '../Media/utils.js'
import { PaginationControls } from '../PaginationControls/index.jsx'
import { SmartLink } from '../SmartLink/SmartLink.jsx'

const articleFragment = cmsGraphql(/* GraphQL */ `
  fragment articleFragment on CmsArticle {
    id
    slug
    title
    readTimeEstimate
    resourceCategory
    _status
    meta {
      image {
        ...CmsMediaFragment
      }
    }
  }
`)

export const collectionArchiveSelectedDocs = cmsGraphql(/* GraphQL */ `
  fragment collectionArchiveSelectedDocs on CmsArchive_SelectedDocs_Relationship {
    relationTo
    value {
      ...articleFragment
    }
  }
`)

export const collectionArchivePopulatedDocs = cmsGraphql(/* GraphQL */ `
  fragment collectionArchivePopulatedDocs on CmsArchive_PopulatedDocs_Relationship {
    relationTo
    value {
      ...articleFragment
    }
  }
`)

export const collectionArchiveCategoriesFragment = cmsGraphql(/* GraphQL */ `
  fragment collectionArchiveCategoriesFragment on CmsCategories {
    id
  }
`)

export type Props = {
  populateBy?: 'collection' | 'selection' | null
  populatedDocs?: FragmentType<typeof collectionArchivePopulatedDocs>[] | null
  selectedDocs: FragmentType<typeof collectionArchiveSelectedDocs>[] | null | undefined
  resourceCategory?: string | null
}

export const CollectionArchive = ({ populateBy, populatedDocs, selectedDocs }: Props) => {
  const docs: (FragmentType<typeof articleFragment> | null)[] | undefined = (
    populateBy === 'collection' ? populatedDocs : populateBy === 'selection' ? selectedDocs : []
  )?.map((doc) => (doc as { value: FragmentType<typeof articleFragment> | null }).value)

  const initialItemsToShow = 6
  const { currentItems, showMoreItems, showLessItems, hasMoreItems, hasLessItems } = useShowMoreLess(
    docs ?? [],
    initialItemsToShow,
  )

  return (
    <div>
      <Gutter>
        <ul
          className={`m-0 gap-200 p-0 md:gap-500 ${currentItems.length < 3 ? 'grid grid-cols-2 md:flex md:justify-center' : 'grid grid-cols-2 md:grid-cols-3'}`}
        >
          {currentItems.map((result) => {
            if (!result) {
              return null
            }

            const { id, title, meta, slug, readTimeEstimate, resourceCategory, _status } = getFragmentData(
              articleFragment,
              result,
            )

            const mediaUrl = getMediaSizeUrlWithFallback(meta?.image, 'card') ?? PLACEHOLDER_IMAGE_URL

            return (
              <li key={id} className="relative w-full max-w-[455px]">
                <SmartLink to={`/${getBlogArticleParentPath(resourceCategory)}/${slug}`} title={title ?? undefined}>
                  {_status === CMSVersionDraft && <Badge className="absolute right-100 top-100 z-20">Draft</Badge>}
                  <div className="relative z-10 flex flex-1 flex-col gap-300 rounded-200 bg-white">
                    {mediaUrl && (
                      <div className="relative aspect-[16/9] overflow-hidden rounded-200">
                        <img
                          src={mediaUrl}
                          alt={getFragmentData(CmsMediaFragment, meta?.image)?.alt}
                          className="size-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex flex-col gap-75 self-stretch">
                      <h3 className="headline font-semibold text-onSurfaceVariant">{title}</h3>{' '}
                      {readTimeEstimate && (
                        <p className="caption1 text-onSurfaceVariant">
                          {readTimeEstimate} minute{readTimeEstimate > 1 ? 's' : ''}
                        </p>
                      )}
                    </div>
                  </div>
                </SmartLink>
              </li>
            )
          })}
        </ul>
        <div className="flex justify-center py-300">
          <PaginationControls
            hasMoreItems={hasMoreItems}
            showMoreItems={showMoreItems}
            hasLessItems={hasLessItems}
            showLessItems={showLessItems}
          />
        </div>
      </Gutter>
    </div>
  )
}
